// HEAL-76 §4.2 `/notifications` — лента уведомлений.
//
// Прочитано = показано (D-6): при монтировании отправляем идентификаторы
// показанных записей, при подгрузке следующей страницы — её идентификаторы.
// НЕ «пометить всё»: при ста непрочитанных человек видит двадцать, и остальные
// восемьдесят он не читал.
//
// Лента работает и в браузере — в отличие от самих push. Это прямое следствие
// того, что запись создаётся независимо от доставки (I-1).

import { useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'wouter';
import { Bell, ChevronRight, Loader2 } from 'lucide-react';
import { useNotifications, useMarkNotificationsRead } from '@/api/hooks/notifications';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { Button } from '@/components/ui/button';
import { targetPath } from '@/lib/pushTarget';
import { cn } from '@/lib/utils';
import type { NotificationItem } from '@/api/types';

function formatWhen(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();

  if (sameDay) {
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  }

  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' });
}

export function NotificationsFeed() {
  const [, setLocation] = useLocation();
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useNotifications();
  const markRead = useMarkNotificationsRead();

  const items = useMemo<NotificationItem[]>(
    () => (data?.pages ?? []).flatMap((page) => page.data),
    [data],
  );

  // Отмечаем прочитанными ровно то, что показали, и ровно один раз за запись:
  // без этой памяти каждый перерисованный список слал бы отметку заново.
  //
  // ⚠️ ДВА НАБОРА, А НЕ ОДИН.
  //
  // `acked` — подтверждённые сервером. `inFlight` — отправленные и ожидающие
  // ответа. Ранняя редакция держала один набор и добавляла в него ДО ответа:
  // при обрыве связи, 429 или 500 записи оставались помеченными навсегда,
  // повтора не было, и человек, прочитавший сообщения, видел прежний бейдж до
  // следующего открытия экрана.
  //
  // Теперь неудачная отметка возвращает записи в оборот: сама мутация повторяет
  // запрос, а если и повторы не прошли — идентификаторы уходят из обоих наборов
  // и отметка уйдёт снова при подгрузке следующей страницы или при следующем
  // открытии ленты.
  const acked = useRef<Set<number>>(new Set());
  const inFlight = useRef<Set<number>>(new Set());

  useEffect(() => {
    const fresh = items
      .filter(
        (item) =>
          item.read_at === null && !acked.current.has(item.id) && !inFlight.current.has(item.id),
      )
      .map((item) => item.id);

    if (fresh.length === 0) return;

    fresh.forEach((id) => inFlight.current.add(id));

    // ⚠️ mutateAsync, а НЕ mutate с колбэками.
    //
    // Колбэки, переданные в mutate(), библиотека вызывает только для последнего
    // вызова: если первая отметка ещё в полёте, а подгрузка следующей страницы
    // запустила вторую, то onError первой не выполнится никогда. Её записи
    // остались бы в inFlight навсегда — то есть ровно тот дефект, ради которого
    // два набора и заводились, только теперь молчаливый.
    //
    // Промис привязан к своему вызову, поэтому каждая отметка разбирает
    // собственные идентификаторы независимо от того, сколько их в полёте.
    void markRead
      .mutateAsync(fresh)
      .then(() => {
        fresh.forEach((id) => acked.current.add(id));
      })
      .catch(() => {
        // Из оборота не выбывают: в `acked` они не попали, а из `inFlight`
        // уходят ниже — отметка уйдёт снова со следующей страницей или при
        // следующем открытии ленты.
      })
      .finally(() => {
        fresh.forEach((id) => inFlight.current.delete(id));
      });
    // markRead в зависимостях не нужен: мутация стабильна, а лишний прогон
    // эффекта отправил бы отметку повторно.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  const open = (item: NotificationItem) => {
    const path = targetPath({
      type: item.type,
      target: item.target,
      targetId: item.target_id,
    });

    if (path) setLocation(path);
  };

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/')} title="Уведомления" />

      <main className="flex-1 overflow-y-auto px-6 pb-10 pt-2">
        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {isError && !isLoading && (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Не удалось загрузить уведомления
          </p>
        )}

        {!isLoading && !isError && items.length === 0 && (
          <div className="py-16 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Bell className="h-6 w-6" />
            </div>
            <p className="mb-1 text-sm font-semibold">Пока ничего не приходило</p>
            <p className="text-hl-small leading-relaxed text-muted-foreground">
              Здесь будут сообщения о том, что анализы обработаны.
            </p>
          </div>
        )}

        <div className="space-y-2">
          {items.map((item) => {
            const path = targetPath({
              type: item.type,
              target: item.target,
              targetId: item.target_id,
            });
            const unread = item.read_at === null;

            return (
              <button
                key={item.id}
                type="button"
                disabled={path === null}
                onClick={() => open(item)}
                className={cn(
                  'flex w-full items-start gap-3 rounded-control border p-3 text-left transition-colors',
                  // Непрочитанное выделяем фоном, а не точкой сбоку: точка в
                  // списке из двух элементов не читается.
                  unread ? 'border-primary/40 bg-primary/5' : 'border-border bg-card',
                  path === null ? 'cursor-default' : 'hover-elevate',
                )}
              >
                <span className="min-w-0 flex-1">
                  <span className="mb-0.5 flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{item.title}</span>
                    {unread && (
                      <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                    )}
                  </span>
                  <span className="block text-hl-small leading-relaxed text-muted-foreground">
                    {item.body}
                  </span>
                  <span className="mt-1 block text-hl-small text-muted-foreground">
                    {formatWhen(item.created_at)}
                  </span>
                </span>

                {path !== null && (
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-muted-foreground" />
                )}
              </button>
            );
          })}
        </div>

        {hasNextPage && (
          <Button
            variant="outline"
            className="mt-4 w-full"
            disabled={isFetchingNextPage}
            onClick={() => void fetchNextPage()}
          >
            {isFetchingNextPage ? 'Загружаем…' : 'Показать ещё'}
          </Button>
        )}
      </main>
    </MobileFrame>
  );
}
